import { useEffect, useState } from "react";
import { ArrowUpRight, Timer, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { Chapter, PartHeading } from "./Chapter";
import { ComparisonMatrix } from "./ComparisonMatrix";

export const OverviewGrid = () => {
  const { d, ui } = useLang();
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpenId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Κάθε κάρτα ανοίγει πλαίσιο με το αντίστοιχο χωρίο της ανάλυσης (όχι μετακίνηση στη σελίδα)
  const renderAnalysis = (id) => {
    if (id === "matrix") return <ComparisonMatrix />;
    const part = [d.PART1, d.PART2, d.PART3, d.PART4].find((p) => p.id === id);
    if (part) {
      return (
        <>
          <PartHeading part={part} />
          {part.chapters.map((c) => (
            <Chapter key={c.id} chapter={c} compact />
          ))}
        </>
      );
    }
    const chapter = [d.PROLOGUE, d.EPILOGUE, ...[d.PART1, d.PART2, d.PART3, d.PART4].flatMap((p) => p.chapters)].find((c) => c.id === id);
    if (chapter) {
      // Στη σύνοψη ο πίνακας μετακίνησης ονομάτων (13.2) παραλείπεται — μένει μόνο στην πλήρη ανάλυση
      if (chapter.id === 'ch13') {
        const blocks = chapter.blocks.filter((b) => b.kind !== 'moves' && !(b.type === 'sub' && b.text.startsWith('13.2')));
        return <Chapter chapter={{ ...chapter, blocks }} />;
      }
      return <Chapter chapter={chapter} />;
    }
    return null;
  };

  const openCard = d.OVERVIEW_CARDS.find((c) => c.id === openId);

  // Διακριτικό fade-up των καρτών όταν μπαίνουν στο οπτικό πεδίο
  useEffect(() => {
    const items = document.querySelectorAll('[data-testid^="overview-card-"]');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      }),
      { threshold: 0.15 }
    );
    items.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  return (
    <section id="overview" data-testid="overview-section" className="border-b border-line bg-surface/50 pb-20 pt-10 md:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-12 flex items-start gap-4">
          <div className="rounded-full bg-aegean p-3 text-paper">
            <Timer className="h-5 w-5" />
          </div>
          <div>
            <p className="eyebrow">{ui.overviewEyebrow}</p>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl font-semibold tracking-tight text-ink">
              {ui.overviewTitle}
            </h2>
            <div className="mt-3 max-w-3xl space-y-4 font-reading text-[1.02rem] leading-[1.8] text-muted-foreground md:text-[1.12rem]">
              <p>{ui.overviewIntro1}</p>
              <p>{ui.overviewIntro2}</p>
            </div>
          </div>
        </div>
        <div className="grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {d.OVERVIEW_CARDS.map((card, i) => (
            <button
              type="button"
              key={card.id}
              onClick={() => setOpenId(card.id)}
              data-testid={`overview-card-${card.id}`}
              style={{ transitionDelay: `${(i % 3) * 70}ms` }}
              className="card-reveal group relative flex flex-col justify-between bg-paper p-6 text-left hover:bg-surface md:p-8"
            >
              <div>
                <span className="font-serif text-4xl font-medium text-line transition-colors duration-200 group-hover:text-terra md:text-5xl">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-4 font-serif text-xl md:text-2xl font-semibold tracking-tight text-ink group-hover:text-aegean">
                  {card.title}
                </h3>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                  {card.teaser}
                </p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] text-terra">
                {ui.overviewCardCta} <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {openId && (
        <div
          data-testid="overview-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-anthra/70 p-4 backdrop-blur-sm"
          onClick={() => setOpenId(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-paper p-6 shadow-2xl md:p-10"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={openCard?.title}
          >
            <div className="mb-6 flex items-center justify-between border-b border-line pb-4">
              <h3 className="font-serif text-xl font-semibold text-ink md:text-2xl">{openCard?.title}</h3>
              <button
                type="button"
                data-testid="overview-modal-close"
                onClick={() => setOpenId(null)}
                className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground transition-colors hover:border-terra hover:text-terra"
              >
                <X className="h-4 w-4" />
                {ui.overviewModalClose}
              </button>
            </div>
            {renderAnalysis(openId)}
          </div>
        </div>
      )}
    </section>
  );
};
